import { Injectable, signal, computed } from '@angular/core';
import { StorageService } from './storage.service';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';

export type MainCategory = 'posta' | 'rubrica' | 'postale' | 'impostazioni';

export type View =
  | 'inbox'
  | 'sent'
  | 'archive'
  | 'drafts'
  | 'contacts'
  | 'postal'
  | 'settings'
  | 'support';

export interface NavItem {
  id: View;
  label: string;
  icon: string;
  route: string;
  category: MainCategory;
}

export interface FooterNavItem {
  id: View;
  label: string;
  icon: string;
  route?: string;
}

export interface NavigationItem {
  label: string;
  category: MainCategory;
  route: string;
}

@Injectable({ providedIn: 'root' })
export class NavigationService {
  constructor(private storageService: StorageService, private http: HttpClient) {}

  currentMainCategory = signal<MainCategory>('posta');
  currentView = signal<View>('inbox');

  userRoleNavigationItems: Record<string, MainCategory[]> = {
    amministratore: ['posta', 'rubrica', 'postale'],
  };

  navigationItems: NavigationItem[] = [
    { label: 'Posta', category: 'posta', route: '/inbox' },
    { label: 'Rubrica', category: 'rubrica', route: '/contacts' },
    { label: 'Postale', category: 'postale', route: '/postal' },
    { label: 'Impostazioni', category: 'impostazioni', route: '/settings' },
  ];

  private readonly allNavItems: NavItem[] = [
    { id: 'inbox', label: 'Da classificare', icon: 'inbox', route: 'inbox', category: 'posta' },
    { id: 'sent', label: 'Inviati', icon: 'send', route: 'sent', category: 'posta' },
    { id: 'drafts', label: 'Bozze', icon: 'edit', route: 'drafts', category: 'posta' },
    { id: 'archive', label: 'Archivio', icon: 'archive', route: 'archive', category: 'posta' },
    { id: 'contacts', label: 'Rubrica', icon: 'book-open', route: 'contacts', category: 'rubrica' },
    ];

  readonly footerNavItems: FooterNavItem[] = [
    { id: 'settings', label: 'Impostazioni', icon: 'cog', route: 'settings' },
  ];

  private readonly permissions = signal<ReadonlySet<string>>(new Set());

  private static readonly PERMISSION_CATEGORY: Record<MainCategory, string> = {
    posta: 'MAILS_READ',
    rubrica: 'CONTACTS_READ',
    postale: 'MAILS_READ',
    impostazioni: 'MAILS_READ',
  };

  sidebarItems = computed<NavItem[]>(() => this.allNavItems.filter(item => this.allowedCategories().includes(item.category)));

  hasSidebarItems = computed(() => this.sidebarItems().length > 0);

  getCategoryForRoute(url: string): MainCategory | undefined {
    return this.allNavItems.find(item => url.includes(item.route))?.category;
  }

  setMainCategory(category: MainCategory) {
    this.currentMainCategory.set(category);
  }

  setView(view: View) {
    this.currentView.set(view);
  }

  loadPermissions(): Observable<void> {
    return this.http.get<string[]>(`${environment.apiUrl}/api/v1/users/this/auth-list`).pipe(
      tap(a => this.permissions.set(new Set(a))),
      map(() => undefined),
    );
  }

  private allowedCategories(): MainCategory[] {
    const a = this.permissions();
    return (Object.keys(NavigationService.PERMISSION_CATEGORY) as MainCategory[])
      .filter(c => a.has(NavigationService.PERMISSION_CATEGORY[c]));
  }
}
